import type { MixedItemDist } from "@memorang/types";
import { useActiveStudyPackCollectionId } from "./useActiveStudyPackCollectionId";
import useMixedItemDist from "./useMixedItemDist";
import { useStudyPackAccess } from "./useStudyPackAccess";

/** Tag structure changes far less often than progress, so it outlives the primary distribution's window. */
const PREMIUM_DIST_STALE_TIME = 30 * 60 * 1000;

/**
 * The paid study-pack collection's distribution, fetched only for a freemium user currently
 * being served the free collection. Lets the UI show what's behind the paywall (categories,
 * item counts) without any backend changes — same endpoint, different `collectionId`.
 *
 * `options.enabled` (default `true`) lets a caller add its own restriction on top of the data
 * condition below — e.g. `useHomeSections` passes `buildType === Apps.Jbjs` so the free-sample
 * home upsell (APP-1623) stays JBJS-only, without narrowing this hook's own shared behavior:
 * `usePremiumStudyPackTags` (session-config's locked-tags preview) also delegates here and must
 * keep working for every partner whose exam happens to have this free/paid collection shape
 * (today: JBJS, TOEIC Link, PTE Core).
 */
export const usePremiumStudyPackDist = (options?: { enabled?: boolean }) => {
	const { hasStudyPackPurchaseAccess } = useStudyPackAccess();
	const { studyPackCollectionId, freeStudyPackCollectionId } =
		useActiveStudyPackCollectionId();

	const enabled =
		(options?.enabled ?? true) &&
		!hasStudyPackPurchaseAccess &&
		Boolean(freeStudyPackCollectionId) &&
		Boolean(studyPackCollectionId) &&
		studyPackCollectionId !== freeStudyPackCollectionId;

	const { data, isLoading } = useMixedItemDist({
		action: "GET_STUDYPACK_DISTRIBUTION",
		collectionId: studyPackCollectionId,
		enabled,
		staleTime: PREMIUM_DIST_STALE_TIME,
		retry: false,
	});

	return {
		enabled,
		isLoading: enabled && isLoading,
		data: enabled ? (data as MixedItemDist | undefined) : undefined,
	};
};
